import React, { useRef } from "react";
import { Search, X } from "lucide-react";
import "./SearchInput.scss";

export type SearchInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "size"> & {
  value: string;
  onChange: (value: string) => void;
  /** Enter pressed (for searches that run on submit) */
  onSubmit?: (value: string) => void;
  /** sm 32px, md 40px (default) */
  size?: "sm" | "md";
  /** Stretch to its container */
  block?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
};

/** A search field with a magnifier and, once it has text, a clear button. */
export function SearchInput({
  value,
  onChange,
  onSubmit,
  size = "md",
  block = false,
  placeholder = "Search…",
  className,
  inputRef,
  onKeyDown,
  ...rest
}: SearchInputProps): React.JSX.Element {
  const ownRef = useRef<HTMLInputElement>(null);
  const input = inputRef ?? ownRef;
  return (
    <div className={["ui-search", `ui-search--${size}`, block ? "ui-search--block" : "", className].filter(Boolean).join(" ")}>
      <Search className="ui-search__icon" size={size === "sm" ? 14 : 16} aria-hidden="true" />
      <input
        {...rest}
        ref={input}
        className="ui-search__input"
        type="search"
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        aria-label={rest["aria-label"] ?? placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onSubmit) onSubmit(value);
          onKeyDown?.(e);
        }}
      />
      {value ? (
        <button
          type="button"
          className="ui-search__clear"
          aria-label="Clear search"
          onClick={() => {
            onChange("");
            onSubmit?.("");
            input.current?.focus();
          }}
        >
          <X size={14} />
        </button>
      ) : null}
    </div>
  );
}

export default SearchInput;
